import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Eye, UserCheck, Mail, ArrowLeft, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-cyan-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Link 
          to="/login" 
          className="inline-flex items-center gap-2 text-cyan-700 hover:text-cyan-900 mb-6 font-medium"
        >
          <ArrowLeft size={20} />
          Back to Login
        </Link>

        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-cyan-100 rounded-full">
              <Shield size={32} className="text-cyan-700" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Privacy Policy</h1>
              <p className="text-slate-600">Last updated: December 13, 2024</p>
            </div>
          </div>
          
          <p className="text-slate-700 leading-relaxed">
            At We Are Pilgrims, we are committed to protecting your privacy and ensuring the security of your personal health information. This Privacy Policy explains how we collect, use, and safeguard your data.
          </p>

          <Alert className="mt-4 bg-blue-50 border-blue-200">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-900">
              <strong>First time here?</strong> After reading this policy, simply close this tab to return to the consent screen where you can accept our terms.
            </AlertDescription>
          </Alert>
        </div>

        {/* Content Sections */}
        <div className="bg-white rounded-2xl shadow-lg p-8 space-y-8">
          
          {/* Section 1 */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <UserCheck size={24} className="text-cyan-600" />
              <h2 className="text-2xl font-bold text-slate-900">1. Information We Collect</h2>
            </div>
            <div className="space-y-3 text-slate-700 leading-relaxed">
              <p><strong>Personal Information:</strong></p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Name, birthdate, sex, and contact details (email, phone, address)</li>
                <li>Login credentials (email and encrypted password)</li>
                <li>Profile photo (optional)</li>
              </ul>
              
              <p className="mt-4"><strong>Health Information:</strong></p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Medical history, health goals, and problem list</li>
                <li>Clinical data (medications, allergies, family history)</li>
                <li>Visit logs and provider notes</li>
                <li>Any health-related information you share with your care team</li>
              </ul>

              <p className="mt-4"><strong>Usage Information:</strong></p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Login times and activity logs</li>
                <li>Device and browser information</li>
                <li>IP addresses for security purposes</li>
              </ul>
            </div>
          </section>

          {/* Section 2 */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Eye size={24} className="text-cyan-600" />
              <h2 className="text-2xl font-bold text-slate-900">2. How We Use Your Information</h2>
            </div>
            <div className="space-y-2 text-slate-700 leading-relaxed">
              <p>We use your information to:</p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li><strong>Provide healthcare services:</strong> Manage your care, coordinate with providers, and maintain your health records</li>
                <li><strong>Communicate with you:</strong> Send appointment reminders, health updates, and important notifications</li>
                <li><strong>Improve our platform:</strong> Analyze usage patterns to enhance user experience</li>
                <li><strong>Ensure security:</strong> Protect against unauthorized access and maintain data integrity</li>
                <li><strong>Comply with legal obligations:</strong> Meet regulatory and legal requirements</li>
              </ul>
            </div>
          </section>

          {/* Section 3 */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Lock size={24} className="text-cyan-600" />
              <h2 className="text-2xl font-bold text-slate-900">3. How We Protect Your Information</h2>
            </div>
            <div className="space-y-2 text-slate-700 leading-relaxed">
              <p>We implement industry-standard security measures:</p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li><strong>Encryption:</strong> All data transmitted between your device and our servers is encrypted using HTTPS/SSL</li>
                <li><strong>Secure passwords:</strong> Passwords are encrypted using bcrypt hashing</li>
                <li><strong>Access controls:</strong> Role-based access ensures only authorized personnel can view your data</li>
                <li><strong>Automatic logout:</strong> Sessions expire after 30 minutes of inactivity</li>
                <li><strong>Regular backups:</strong> Your data is backed up daily to prevent loss</li>
                <li><strong>Audit logs:</strong> All access to sensitive data is logged for security monitoring</li>
              </ul>
            </div>
          </section>

          {/* Section 4 */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <UserCheck size={24} className="text-cyan-600" />
              <h2 className="text-2xl font-bold text-slate-900">4. Who Can Access Your Information</h2>
            </div>
            <div className="space-y-2 text-slate-700 leading-relaxed">
              <p><strong>Your Care Team:</strong></p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Providers assigned to your care can view and update your health records</li>
                <li>Administrative staff can access basic information for scheduling and coordination</li>
              </ul>
              
              <p className="mt-4"><strong>We DO NOT:</strong></p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Sell your personal or health information to third parties</li>
                <li>Share your data with advertisers or marketers</li>
                <li>Use your information for purposes unrelated to your healthcare</li>
              </ul>
            </div>
          </section>

          {/* Section 5 */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Shield size={24} className="text-cyan-600" />
              <h2 className="text-2xl font-bold text-slate-900">5. Your Rights (Philippine Data Privacy Act)</h2>
            </div>
            <div className="space-y-2 text-slate-700 leading-relaxed">
              <p>Under the Philippine Data Privacy Act of 2012, you have the right to:</p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li><strong>Access:</strong> View and obtain copies of your personal data</li>
                <li><strong>Correction:</strong> Request corrections to inaccurate or incomplete information</li>
                <li><strong>Erasure:</strong> Request deletion of your data (subject to legal retention requirements)</li>
                <li><strong>Object:</strong> Object to certain processing of your data</li>
                <li><strong>Data portability:</strong> Receive your data in a structured, commonly used format</li>
                <li><strong>Lodge complaints:</strong> File complaints with the National Privacy Commission</li>
              </ul>
              
              <p className="mt-4">
                To exercise any of these rights, please contact us at the email address provided below.
              </p>
            </div>
          </section>

          {/* Section 6 */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Mail size={24} className="text-cyan-600" />
              <h2 className="text-2xl font-bold text-slate-900">6. Data Retention</h2>
            </div>
            <div className="space-y-2 text-slate-700 leading-relaxed">
              <p>We retain your information for as long as:</p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Your account is active and you are receiving our services</li>
                <li>Required by law for medical record retention (minimum 5 years in the Philippines)</li>
                <li>Necessary for legal, audit, or compliance purposes</li>
              </ul>
              <p className="mt-3">
                After account closure, we may anonymize your data for research and quality improvement purposes.
              </p>
            </div>
          </section>

          {/* Section 7 */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Shield size={24} className="text-cyan-600" />
              <h2 className="text-2xl font-bold text-slate-900">7. Cookies and Tracking</h2>
            </div>
            <div className="space-y-2 text-slate-700 leading-relaxed">
              <p>We use essential cookies and local storage to:</p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Keep you logged in during your session</li>
                <li>Remember your preferences</li>
                <li>Ensure the security of our platform</li>
              </ul>
              <p className="mt-3">
                We do not use tracking cookies for advertising purposes.
              </p>
            </div>
          </section>

          {/* Section 8 */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Lock size={24} className="text-cyan-600" />
              <h2 className="text-2xl font-bold text-slate-900">8. Data Breach Notification</h2>
            </div>
            <div className="space-y-2 text-slate-700 leading-relaxed">
              <p>
                In the unlikely event of a data breach that affects your personal information, we will:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Notify affected users within 72 hours of discovery</li>
                <li>Report to the National Privacy Commission as required by law</li>
                <li>Take immediate steps to secure the breach and prevent further unauthorized access</li>
                <li>Provide guidance on protective measures you can take</li>
              </ul>
            </div>
          </section>

          {/* Section 9 */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <UserCheck size={24} className="text-cyan-600" />
              <h2 className="text-2xl font-bold text-slate-900">9. Changes to This Policy</h2>
            </div>
            <div className="space-y-2 text-slate-700 leading-relaxed">
              <p>
                We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. 
                We will notify you of any material changes by:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Posting the updated policy on our platform</li>
                <li>Sending you an email notification</li>
                <li>Displaying a prominent notice on your dashboard</li>
              </ul>
              <p className="mt-3">
                Your continued use of our services after such changes constitutes acceptance of the updated policy.
              </p>
            </div>
          </section>

          {/* Contact Section */}
          <section className="bg-cyan-50 rounded-xl p-6 border-2 border-cyan-200">
            <div className="flex items-center gap-2 mb-3">
              <Mail size={24} className="text-cyan-700" />
              <h2 className="text-2xl font-bold text-slate-900">10. Contact Us</h2>
            </div>
            <div className="space-y-2 text-slate-700 leading-relaxed">
              <p>
                If you have questions about this Privacy Policy or wish to exercise your data rights, please contact us:
              </p>
              <div className="mt-4 space-y-2">
                <p className="font-semibold text-slate-900">Dr. TJ Malvar, Founder</p>
                <p className="font-medium">Pilgrim Health Virtual Primary Care Clinic</p>
                <p><strong>Email:</strong> pilgrimhealthph@gmail.com</p>
                <p><strong>Mobile:</strong> 0929-804-4770</p>
              </div>
              <div className="mt-6 pt-4 border-t border-cyan-200">
                <p className="text-sm">
                  <strong>National Privacy Commission (Philippines):</strong><br/>
                  For complaints or inquiries about data privacy: <a href="https://privacy.gov.ph" className="text-cyan-700 underline font-medium" target="_blank" rel="noopener noreferrer">privacy.gov.ph</a>
                </p>
              </div>
            </div>
          </section>

        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-slate-600">
          <p className="mb-2">© 2024 We Are Pilgrims. All rights reserved.</p>
          <div className="flex justify-center gap-4">
            <Link to="/terms" className="text-cyan-700 hover:text-cyan-900 underline">
              Terms of Service
            </Link>
            <span>|</span>
            <Link to="/login" className="text-cyan-700 hover:text-cyan-900 underline">
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
